
import { QueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// Get the Supabase project reference from the URL
const SUPABASE_PROJECT_REF = "pmmrezxcyemynydfiatc";

/**
 * Utility to clear auth-related storage and reset state
 */
export const clearAuthStorage = (queryClient?: QueryClient) => {
  // Flag to prevent cleaning up the same session multiple times
  const cleanupKey = 'auth-cleanup-performed';
  if (sessionStorage.getItem(cleanupKey) === 'true') {
    console.log("Auth cleanup already performed in this session, skipping");
    return false;
  }
  
  console.log("Clearing auth-related storage and state");
  
  // Mark that we've performed cleanup in this session
  sessionStorage.setItem(cleanupKey, 'true');
  
  // Clear Supabase-specific items
  const supabaseKeys = Object.keys(localStorage).filter(key => 
    key.startsWith('sb-') || 
    key.includes('supabase') || 
    key.includes('auth')
  );
  
  supabaseKeys.forEach(key => {
    console.log(`Removing localStorage item: ${key}`);
    localStorage.removeItem(key);
  });
  
  // Explicitly remove the project-specific Supabase token
  const specificTokenKey = `sb-${SUPABASE_PROJECT_REF}-auth-token`;
  if (localStorage.getItem(specificTokenKey)) {
    console.log(`Removing specific Supabase token: ${specificTokenKey}`);
    localStorage.removeItem(specificTokenKey);
  }
  
  // Clear custom app storage
  if (typeof window !== 'undefined' && window.setSafeStorage) {
    window.setSafeStorage('auth-session', null);
  }
  
  // Clear React Query cache if available
  if (queryClient) {
    queryClient.clear();
  }
  
  // Attempt to sign out - but only if we haven't already triggered a signout
  // Use a try-catch to prevent this from breaking execution
  try {
    // Use a flag to prevent recursive signouts
    if (!window.isSigningOut) {
      window.isSigningOut = true;
      
      // Use a non-await signout to prevent blocking
      supabase.auth.signOut().catch(e => {
        console.error("Error during signout:", e);
      }).finally(() => {
        // Reset the flag after a delay
        setTimeout(() => {
          window.isSigningOut = false;
        }, 1000);
      });
    }
  } catch (err) {
    console.error("Exception during signout:", err);
  }
  
  return true;
};

// Add the missing property to the Window interface
declare global {
  interface Window {
    isSigningOut?: boolean;
  }
}

// Reset counter periodically
let requestCounter = 0;
const requestThreshold = 10;
const timeWindow = 5000; // 5 seconds

const counterResetInterval = setInterval(() => {
  if (requestCounter > 0) {
    console.log(`Resetting auth request counter. Was at ${requestCounter}`);
    requestCounter = 0;
  }
}, timeWindow);

// Setup global error handler for auth errors
export const setupAuthErrorHandling = () => {
  // Global error event listener
  window.addEventListener('error', (event) => {
    // Check if the error is related to auth/storage
    const errorMsg = (event.message || '').toLowerCase();
    const isAuthError = errorMsg.includes('auth') || 
                       errorMsg.includes('token') ||
                       errorMsg.includes('session') ||
                       errorMsg.includes('supabase');
                     
    if (isAuthError) {
      console.warn('Auth-related error detected:', errorMsg);
      clearAuthStorage();
    }
  });
  
  // Override fetch to monitor auth requests
  const originalFetch = window.fetch;
  window.fetch = function(...args) {
    const url = args[0]?.toString() || '';
    
    // Check if this is an auth-related request
    const isAuthRequest = url.includes('auth') || 
                          url.includes('token') || 
                          url.includes('session') ||
                          url.includes('supabase');
    
    if (isAuthRequest) {
      requestCounter++;
      console.log(`Auth request detected (${requestCounter}/${requestThreshold}): ${url}`);
      
      // If we detect too many auth requests in a short time, clear storage
      if (requestCounter >= requestThreshold) {
        console.warn(`Potential auth request loop detected (${requestCounter} requests in ${timeWindow/1000}s)`);
        clearAuthStorage();
        requestCounter = 0;
      }
    }
    
    return originalFetch.apply(this, args);
  };
  
  return () => {
    // Cleanup
    clearInterval(counterResetInterval);
  };
};

export default {
  clearAuthStorage,
  setupAuthErrorHandling
};
